import { zodResolver } from '@hookform/resolvers/zod';
import Grid from '@mui/material/Grid';
import { useEffect, useRef } from 'react';
import { type FieldErrors, type SubmitHandler, useForm } from 'react-hook-form';
import Button, {
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import FormInput, {
  InputType,
} from '@/components/BaseLibrary/FormInput/FormInput';
import { LeadFormTCPA } from '@/components/Form/LeadFormTCPA/LeadFormTCPA';
import GridContainer from '@/components/Layout/GridContainer/GridContainer';
import useViewPortSize from '@/hooks/useViewportSize';
import useGlobalStyles from '@/styles/global.styles';
import { trackEvents } from '@/utils/analytics';
import { MAX_IMAGE_WIDTH, setImgWidth } from '@/utils/frontify';
import type { PaidLanderFields } from '../PaidLander.types';
import {
  SubmitStepSchemaMode,
  type SubmitStepSchemaType,
  submitStepSchema,
} from '../schema';
import useStyles from './SubmitStep.styles';

enum InputName {
  FULL_NAME = 'fullName',
  EMAIL = 'email',
  PHONE = 'phone',
  OWN_HOME = 'ownHome',
  // HONEYPOT FIELD
  ADDRESS = 'address',
}

// acts as a type guard for narrowing down error object union type + controls field visibility
const shouldShowEmailField = (
  mode: string,
  _errors: FieldErrors<SubmitStepSchemaType>
): _errors is FieldErrors<
  Extract<
    SubmitStepSchemaType,
    {
      mode: SubmitStepSchemaMode.WITH_EMAIL;
    }
  >
> => {
  return mode === SubmitStepSchemaMode.WITH_EMAIL;
};

export default function SubmitStep({
  secondStepHeader,
  secondStepSubHeading,
  tcpa,
  secondStepImage,
  secondStepImageAltText,
  submitFormText,
  submit,
  fullNameLabel = 'Full name',
  emailLabel = 'Email',
  phoneNumberLabel = 'Phone number',
  isOnDark = false,
  fixedGrid = false,
  isVisible = false,
  reverseLayout = false,
  hideEmailInput = false,
  hideStepCounter = false,
  hideImageOnMobile = false,
}: PaidLanderFields & {
  submit: (data: SubmitStepSchemaType) => void;
  isVisible: boolean;
}) {
  const {
    watch,
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<SubmitStepSchemaType>({
    resolver: zodResolver(submitStepSchema),
    mode: 'onTouched',
    defaultValues: {
      mode: hideEmailInput
        ? SubmitStepSchemaMode.WITHOUT_EMAIL
        : SubmitStepSchemaMode.WITH_EMAIL,
    },
  });

  const { classes, cx } = useStyles({
    isOnDark,
    hideImageOnMobile,
  });
  const { classes: globalClasses } = useGlobalStyles({ fixed: false });
  const { isMobile } = useViewPortSize();
  const imageSize = isMobile ? 768 : 1440;

  const onSubmit: SubmitHandler<SubmitStepSchemaType> = (data) => {
    trackEvents({
      event: 'Form_cta_click',
      Form_tab: 'First Name/Last Name/Email/Phone',
      Form_step: 2,
      click_text: 'Submit',
    });

    submit(data);
  };

  const inputRef = useRef<HTMLInputElement>(null);
  const mode = watch('mode');

  useEffect(() => {
    if (isVisible) inputRef.current?.focus();
  }, [isVisible]);

  return (
    <GridContainer
      gridProps={{
        className: cx(
          isVisible ? classes.visible : classes.hidden,
          reverseLayout ? classes.reverseLayout : classes.layout
        ),
      }}
      fixed={fixedGrid}
    >
      <Grid
        className={cx(reverseLayout && classes.reverseContent)}
        size={{
          xs: 12,
          sm: 9,
          md: 6,
        }}
      >
        {!hideStepCounter && (
          <div className={cx(globalClasses.chip, classes.step)}>
            step 2 of 2
          </div>
        )}

        <div
          className={cx(
            fixedGrid ? globalClasses.h2 : globalClasses.h1,
            classes.heading
          )}
        >
          {secondStepHeader}
        </div>
        <div className={cx(globalClasses.bodyLg, classes.subheading)}>
          {secondStepSubHeading}
        </div>
        <div className={classes.inputGroup}>
          <div className={classes.inputContainer}>
            <input type='hidden' {...register('mode')} />
            <div className={classes.input}>
              <FormInput
                label={fullNameLabel}
                name={InputName.FULL_NAME}
                testId='full-name-input'
                type={InputType.TEXT}
                inputMode='text'
                isOnDark={isOnDark}
                error={errors[InputName.FULL_NAME]?.message}
                register={register}
                inputRef={inputRef}
              />
            </div>
          </div>
          <div className={cx(classes.inputContainer, classes.hidden)}>
            <div className={classes.input}>
              <FormInput
                aria-hidden='true'
                label='Address'
                name={InputName.ADDRESS}
                testId='address'
                type={InputType.TEXT}
                inputMode='text'
                isOnDark={isOnDark}
                error={errors[InputName.ADDRESS]?.message}
                register={register}
                autoComplete='off'
              />
            </div>
          </div>
          <div className={classes.inputContainer}>
            {/* Only render email input if not hidden */}
            {shouldShowEmailField(mode, errors) && (
              <div className={classes.input}>
                <FormInput
                  label={emailLabel}
                  name={InputName.EMAIL}
                  testId='email-input'
                  type={InputType.TEXT}
                  inputMode='email'
                  isOnDark={isOnDark}
                  error={errors[InputName.EMAIL]?.message}
                  register={register}
                />
              </div>
            )}
            <div className={classes.input}>
              <FormInput
                label={phoneNumberLabel}
                testId='phone-input'
                type={InputType.TEL}
                inputMode='tel'
                isOnDark={isOnDark}
                error={errors[InputName.PHONE]?.message}
                name={InputName.PHONE}
                register={register}
              />
            </div>
          </div>
        </div>
        {tcpa && <LeadFormTCPA text={tcpa} className={classes.tcpa} />}
        <Button
          testId='submit-button'
          type='submit'
          variant={ButtonVariant.PRIMARY}
          label={submitFormText}
          size={ButtonSize.MD}
          onClick={handleSubmit(onSubmit)}
          className={classes.button}
          disabled={Object.keys(errors).length > 0 || !isValid}
          isOnDark={isOnDark}
        />
      </Grid>
      <Grid
        className={classes.hideImageOnMobile}
        size={{
          xs: 12,
          md: 6,
        }}
      >
        {/** biome-ignore lint/performance/noImgElement: Automated migration to Biome */}
        <img
          src={setImgWidth(secondStepImage?.[0].preview_url, MAX_IMAGE_WIDTH)}
          className={classes.landerImage}
          alt={secondStepImageAltText}
          width={imageSize}
        />
      </Grid>
    </GridContainer>
  );
}
